import { useEffect } from 'preact/hooks';

/**
 * Default processing-state copy. Deliberately reassuring and explicit about NOT
 * paying again — a shopper who thinks a still-settling payment failed is the
 * single most expensive UX mistake in payments (duplicate charge). Operators
 * can override per-tenant later (locale-keyed, like checkoutLoading); these are
 * the safe defaults. (3Cs — Conversational.)
 */
export const RETURN_PROCESSING_COPY = {
  title: 'Confirming your payment…',
  body:
    "We've received your payment and are confirming it with your bank. " +
    'This can take up to a minute — please keep this page open and do not pay again.',
  stillWaiting:
    'Still confirming your payment. You don’t need to do anything — we’ll email your ' +
    'receipt as soon as it’s confirmed. Please do not pay again.',
};

const PROCESSING_FALLBACK = { maxAttempts: 6, retryDelayMs: 3000 };

/**
 * Decide whether the processing page should auto re-check the payment status,
 * and where to navigate next. Pure + total so it's unit-testable without a DOM
 * or timers. The server hands us `return_to` (the public /return URL), the
 * current `attempt`, the per-tenant `max`, and the `delay`.
 */
export function nextProcessingCheck(params: URLSearchParams): {
  retry: boolean;
  delayMs: number;
  nextUrl: string | null;
} {
  const returnTo = params.get('return_to');
  const attempt = Number(params.get('attempt') ?? '0') || 0;
  const max = Number(params.get('max') ?? '') || PROCESSING_FALLBACK.maxAttempts;
  const delayMs = Number(params.get('delay') ?? '') || PROCESSING_FALLBACK.retryDelayMs;
  if (!returnTo || attempt >= max) return { retry: false, delayMs, nextUrl: null };
  try {
    const u = new URL(returnTo);
    u.searchParams.set('attempt', String(attempt + 1));
    return { retry: true, delayMs, nextUrl: u.toString() };
  } catch {
    // A malformed return_to must never strand the shopper on a spinner forever.
    return { retry: false, delayMs, nextUrl: null };
  }
}

function ProcessingPanel() {
  const params = new URLSearchParams(window.location.search);
  const piId = params.get('payment_intent_id');
  const plan = nextProcessingCheck(params);
  // Derived, not state: `plan` is recomputed each render from the (navigation-
  // only) URL, so exhaustion is a pure function of it.
  const exhausted = !plan.retry || !plan.nextUrl;

  useEffect(() => {
    if (exhausted) return;
    const t = setTimeout(() => {
      // Full-page navigation back to the public /return URL: the API re-polls
      // (bounded) and either 303s to success/failure (terminal) or back here
      // with attempt+1. The shopper never sees a failure while still settling.
      window.location.assign(plan.nextUrl as string);
    }, plan.delayMs);
    return () => clearTimeout(t);
  }, [exhausted, plan.nextUrl, plan.delayMs]);

  return (
    <div class="w-full max-w-md bg-surface-container-lowest border border-outline-variant rounded-xl p-6 shadow-sm">
      <div class="flex items-center gap-3 mb-3">
        <span
          class="inline-block w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin"
          aria-hidden="true"
        />
        <h1 class="text-h2 font-semibold tracking-tight">{RETURN_PROCESSING_COPY.title}</h1>
      </div>
      <p class="text-body-sm text-on-surface-variant" aria-live="polite">
        {exhausted ? RETURN_PROCESSING_COPY.stillWaiting : RETURN_PROCESSING_COPY.body}
      </p>
      {piId && (
        <p class="text-body-sm text-on-surface-variant mt-3">
          Reference: <code class="upg-mono">{piId}</code>
        </p>
      )}
    </div>
  );
}

export function ReturnPage(props: { outcome: string }) {
  // Still-settling payment: reassure + re-check. NEVER the failure UI — that
  // would invite a duplicate payment.
  if (props.outcome === 'processing') return <ProcessingPanel />;

  const titles: Record<string, { title: string; tone: 'success' | 'failure' | 'cancel' }> = {
    success: { title: '✅ Payment successful', tone: 'success' },
    failure: { title: '❌ Payment failed', tone: 'failure' },
    cancel: { title: '⚠️ Payment cancelled', tone: 'cancel' },
  };
  const t = titles[props.outcome] ?? { title: 'Payment status', tone: 'cancel' as const };
  const params = new URLSearchParams(window.location.search);
  const piId = params.get('payment_intent_id');
  const status = params.get('status');
  return (
    <div class="w-full max-w-md bg-surface-container-lowest border border-outline-variant rounded-xl p-6 shadow-sm">
      <h1 class="text-h2 font-semibold tracking-tight mb-2">{t.title}</h1>
      {piId && (
        <p class="text-body-sm text-on-surface-variant mb-1">
          Reference: <code class="upg-mono">{piId}</code>
        </p>
      )}
      {status && (
        <p class="text-body-sm">
          Status: <strong>{status}</strong>
        </p>
      )}
      <p class="text-body-sm text-on-surface-variant mt-3">You may now close this window.</p>
    </div>
  );
}
